import { useEffect, useState } from "react";
import type { UploadedFileDto } from "../../api/client";
import { ApiError, fetchUploadedFiles } from "../../api/client";

export type GalleryFilter = "all" | "posters";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (file: UploadedFileDto) => void;
  formIdentifier?: string;
};

export function AdminGalleryPicker({
  open,
  onClose,
  onSelect,
  formIdentifier = "admin-event-poster",
}: Props) {
  const [files, setFiles] = useState<UploadedFileDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<GalleryFilter>("all");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    fetchUploadedFiles()
      .then((rows) => setFiles(rows.filter((f) => f.mimetype.startsWith("image/"))))
      .catch((e) => setError(e instanceof ApiError ? e.message : "Could not load gallery"))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const filtered =
    filter === "posters"
      ? files.filter((f) => f.formIdentifier === formIdentifier)
      : files;

  return (
    <div className="admin-gallery-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="admin-gallery-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-gallery-title"
      >
        <header className="admin-gallery-modal-header">
          <h3 id="admin-gallery-title">Choose from gallery</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="admin-gallery-filters">
          <button
            type="button"
            className={`admin-gallery-filter${filter === "all" ? " admin-gallery-filter--active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All images
          </button>
          <button
            type="button"
            className={`admin-gallery-filter${filter === "posters" ? " admin-gallery-filter--active" : ""}`}
            onClick={() => setFilter("posters")}
          >
            Event posters
          </button>
        </div>

        {error && <div className="form-error">{error}</div>}
        {loading ? (
          <div style={{ opacity: 0.6, padding: 24 }}>Loading gallery…</div>
        ) : filtered.length === 0 ? (
          <div style={{ opacity: 0.6, padding: 24 }}>No images found. Upload a poster first.</div>
        ) : (
          <div className="admin-gallery-grid">
            {filtered.map((file) => {
              const thumb =
                file.thumbnails.card ?? file.thumbnails.medium ?? file.thumbnails.small ?? file.uploadsUrl;
              return (
                <button
                  key={file.id}
                  type="button"
                  className="admin-gallery-item"
                  onClick={() => {
                    onSelect(file);
                    onClose();
                  }}
                  title={file.originalName}
                >
                  <img src={thumb} alt="" loading="lazy" />
                  <span className="admin-gallery-item-name">{file.originalName}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
